import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_NAMES,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  type SupportedLanguage,
} from '../../i18n';
import { apiBackend } from './api-backend';

interface LanguageContextValue {
  /** Current language code */
  currentLanguage: SupportedLanguage;
  /** All supported languages */
  supportedLanguages: readonly string[];
  /** Language display names */
  languageNames: typeof LANGUAGE_NAMES;
  /** Change the current language */
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  /** Whether translations are loading */
  isLoading: boolean;
  /** Force refresh translations from server */
  refreshTranslations: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
  children: React.ReactNode;
  /** User's preferred language from profile (if logged in) */
  userPreferredLanguage?: string | null;
  /** Callback when language changes - use to sync with backend */
  onLanguageChange?: (lang: string) => void;
}

/**
 * LanguageProvider - Manages application language state
 *
 * Features:
 * - Syncs with i18next
 * - Supports user preference from profile (logged in users)
 * - Falls back to browser language detection
 * - Persists preference in localStorage
 * - Provides cache refresh capability
 */
export function LanguageProvider({
  children,
  userPreferredLanguage,
  onLanguageChange,
}: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  // Determine current language
  const currentLanguage = useMemo(() => {
    const lang = i18n.language?.split('-')[0]; // Handle 'en-US' -> 'en'
    return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)
      ? (lang as SupportedLanguage)
      : DEFAULT_LANGUAGE;
  }, [i18n.language]);

  // Sync with user preference when it changes
  useEffect(() => {
    if (
      userPreferredLanguage &&
      SUPPORTED_LANGUAGES.includes(userPreferredLanguage as SupportedLanguage) &&
      userPreferredLanguage !== currentLanguage
    ) {
      i18n.changeLanguage(userPreferredLanguage);
    }
  }, [userPreferredLanguage, currentLanguage, i18n]);

  // Change language handler
  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      if (!SUPPORTED_LANGUAGES.includes(lang)) {
        console.warn(`Unsupported language: ${lang}`);
        return;
      }

      setIsLoading(true);

      try {
        await i18n.changeLanguage(lang);

        // Persist to localStorage
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        } catch (e) {
          // Ignore localStorage errors
        }

        // Notify parent if needed (e.g., to save to user profile)
        onLanguageChange?.(lang);
      } catch (error) {
        console.error('Failed to change language:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [i18n, onLanguageChange]
  );

  // Refresh translations from server
  const refreshTranslations = useCallback(async () => {
    setIsLoading(true);

    try {
      await apiBackend.refreshTranslations(currentLanguage);
      // Force i18next to reload
      await i18n.reloadResources(currentLanguage);
    } catch (error) {
      console.error('Failed to refresh translations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentLanguage, i18n]);

  const contextValue = useMemo<LanguageContextValue>(
    () => ({
      currentLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES,
      languageNames: LANGUAGE_NAMES,
      changeLanguage,
      isLoading,
      refreshTranslations,
    }),
    [currentLanguage, changeLanguage, isLoading, refreshTranslations]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access language context
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
}

export default LanguageProvider;
